import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Direction_Enum,
  Field_Enum,
  Operator_Enum,
} from '@weave/schema-gen-ts/dist/schemas/appointments/v3/service.pb';
import { DestinationType_Enum } from '@weave/schema-gen-ts/dist/schemas/messaging/shared/v1/enums.pb';
import {
  DynamicFieldProperty_Enum,
  TemplateType_Slug,
} from '@weave/schema-gen-ts/dist/schemas/messaging/templator/v2/model.pb';
import { PropertyBinding } from '@weave/schema-gen-ts/dist/schemas/messaging/templator/v2/service.pb';
import { Person } from '@weave/schema-gen-ts/dist/schemas/persons/v3/persons.pb';
import { RelatedType } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/enums.pb';
import { Vertical } from '@weave/schema-gen-ts/dist/shared/vertical/vertical.pb';
import { isEqual } from 'lodash-es';
import { AppointmentsApiQueries } from '@frontend/api-appointments';
import { PersonsV3 } from '@frontend/api-person';
import { PetsV1 } from '@frontend/api-pets';
import { TemplatorV2Hooks, TemplatorV2Queries } from '@frontend/api-templator-v2';
import {
  getPetBindingFromAppointmentData,
  LinkData,
  PropertyBindingsData,
  SETTINGS_MODAL_INTERACTION_DELAY,
  TemplateSelectionModalProps,
} from '@frontend/integrated-messaging';
import { useLastUsedVerticalShallowStore } from '@frontend/location-helpers';
import { MessagingSelectInvoiceModal } from '@frontend/payments-collection-flow';
import { useAppScopeStore } from '@frontend/scope';
import { FeatureUpgradeModalWrapper, useFormsTeaserConfig } from '@frontend/self-upgrade';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { useAppFlagStore } from '@frontend/shared';
import { StateRefHelpers } from '@frontend/state-ref-helpers';
import { ComponentProps } from '@frontend/types';
import { FormsTemplateModal } from '../components/forms-template-modal';
import { ScheduleTemplateModal } from '../components/schedule-template-modal';
import { RenderTemplateArgs, TemplateFlowPopoverItem } from '../types';
import { useFormsPromotionFlow } from './use-forms-promotion-flow';
import { useFormsTemplateFlow } from './use-forms-template-flow';
import { useManualTemplateFlow } from './use-manual-template-flow';
import { useReviewInvitationTemplateFlow } from './use-review-invitation-template-flow';
import { useScheduleTemplateFlow } from './use-schedule-template-flow';
import { useTextToPayTemplateFlow } from './use-text-to-pay-template-flow';

type UseInboxTemplateFlowsArgs = {
  groupId: string;
  contextPersonId?: string;
  personPhone: string;
  locationPhone: string;
  onRenderTemplate: (args: { bodyValue: string; linkData?: LinkData[] }) => void;
  petId?: string;
};

export type UseInboxTemplateFlowsResults = {
  availableTemplateFlows: TemplateFlowPopoverItem[];
  person?: Person;
  isPrimaryContact: boolean;
  canChangeContextContact: boolean;
  modalsProps: {
    manualTemplateModalProps: TemplateSelectionModalProps;
    scheduleTemplateModalProps: ComponentProps<typeof ScheduleTemplateModal>;
    reviewInvitationModalProps: TemplateSelectionModalProps;
    selectInvoiceModalProps: ComponentProps<typeof MessagingSelectInvoiceModal>;
    formsTemplateModalProps: ComponentProps<typeof FormsTemplateModal>;
    formsPromotionModalProps: ComponentProps<typeof FeatureUpgradeModalWrapper>;
  };
};

export const useInboxTemplateFlows = ({
  groupId,
  contextPersonId,
  personPhone,
  locationPhone,
  onRenderTemplate,
  petId,
}: UseInboxTemplateFlowsArgs): UseInboxTemplateFlowsResults => {
  const { getLocationName, selectedOrgId, accessibleLocationData } = useAppScopeStore();
  const { isOpen } = useSettingsNavigate();
  const { lastUsedVertical } = useLastUsedVerticalShallowStore('lastUsedVertical');
  const { featureFlags } = useAppFlagStore();
  const isVet = lastUsedVertical === Vertical.VET;
  const petNameEnabled = !!featureFlags[groupId]?.get('nwx:manual-templates-pet-name')?.value;

  const { data: primaryContactId } = PersonsV3.PersonQueries.useGetPrimaryContactLegacyQuery(
    {
      phoneNumber: personPhone,
      locationId: groupId,
    },
    { enabled: !contextPersonId, select: (data) => data.personId }
  );
  const personId = contextPersonId || primaryContactId;
  const { data: preferredPersonsCount = 0 } = PersonsV3.PersonQueries.useListPreferredPersonsByPhoneQuery(
    {
      phoneNumber: personPhone,
      locationId: groupId,
    },
    { enabled: !!personPhone && !!groupId, select: (data) => data.persons.length }
  );
  const canChangeContextContact = preferredPersonsCount > 1;

  const { data: person } = PersonsV3.PersonQueries.useGetPersonLegacyQuery(
    {
      personId: personId ?? '',
      locationIds: [groupId],
    },
    {
      enabled: !!personId && !!groupId,
    }
  );
  const petsQuery = PetsV1.Queries.useFetchPetsByPersonIdQuery(
    {
      personId: personId ?? '',
      locationId: groupId,
    },
    {
      enabled: !!personId && isVet && petNameEnabled && !!groupId,
    }
  );
  const { timestamp } = StateRefHelpers.useStableRoundedTimestamp({ roundingDirection: 'up' });
  const { data: latestPastAppointment } = AppointmentsApiQueries.useListAppointments(
    {
      filters: [
        {
          field: Field_Enum.PERSON_ID,
          operator: Operator_Enum.EQUAL,
          value: personId ?? '',
        },
        {
          field: Field_Enum.APPOINTMENT_AT,
          operator: Operator_Enum.LT,
          value: timestamp.toISOString(),
        },
        !!petId && { field: Field_Enum.SERVICEABLE_ID, operator: Operator_Enum.EQUAL, value: petId },
      ].filter((item) => !!item),
      sorts: [
        {
          field: Field_Enum.APPOINTMENT_AT,
          direction: Direction_Enum.DESC,
        },
      ],
      locationId: groupId,
      limit: 1,
    },
    {
      enabled: !!personId && !!isVet && !!timestamp && !!groupId,
    }
  );

  // Used to override the `show` modal prop with 'hidden' to avoid resetting the state of the modal when closed
  const [hideModal, setHideModal] = useState(false);
  const defaultPropertyBindingsData = TemplatorV2Hooks.useDefaultPropertyBindingsData({
    businessGroupIds: [groupId],
    orgId: selectedOrgId,
  });
  const timezone = accessibleLocationData[groupId]?.timezone ?? '';
  const [renderData, setRenderData] = useState<{
    payload: Parameters<(typeof TemplatorV2Queries)['useRender']>[0];
    linkData?: LinkData[];
  }>();
  const renderedTemplateQuery = TemplatorV2Queries.useRender(
    renderData?.payload ?? {
      templateTypeSlug: TemplateType_Slug.UNSPECIFIED_TEMPLATE_TYPE,
      templateString: '',
      bindingsList: [],
      timezone,
      // TODO: get the locale dynamically once we have a way to do so
      locale: 'en_US',
      destinationType: DestinationType_Enum.SMS,
      keepEmptyDynamicFields: false,
    },
    {
      enabled: !!renderData && !!groupId,
      onSuccess: (data) => {
        onRenderTemplate({
          bodyValue: data.message.message,
          linkData: renderData?.linkData,
        });
      },
    }
  );

  const getBindingsList = useCallback(
    (additionalBindings: PropertyBindingsData = {}): PropertyBinding[] => {
      const contextPropertyBindings: PropertyBindingsData = {
        [DynamicFieldProperty_Enum.ORG_NAME]: getLocationName(selectedOrgId),
        [DynamicFieldProperty_Enum.BUSINESS_GROUP_NAME]: getLocationName(groupId),
        [DynamicFieldProperty_Enum.BUSINESS_GROUP_PHONE]: locationPhone,
      };

      if (person) {
        contextPropertyBindings[DynamicFieldProperty_Enum.FIRST_NAME] = person.firstName;
        contextPropertyBindings[DynamicFieldProperty_Enum.LAST_NAME] = person.lastName;
        contextPropertyBindings[DynamicFieldProperty_Enum.PREFERRED_NAME] = person.preferredName || person.firstName;
      }

      return Object.entries({
        ...defaultPropertyBindingsData,
        ...contextPropertyBindings,
        ...additionalBindings,
      }).reduce<PropertyBinding[]>((acc, [property, value]) => {
        const typedProperty = Object.keys(DynamicFieldProperty_Enum).includes(property)
          ? (property as DynamicFieldProperty_Enum)
          : undefined;
        if (typedProperty && value)
          acc.push({
            property: typedProperty,
            values: [value],
          });
        return acc;
      }, []);
    },
    [groupId, person, locationPhone, selectedOrgId, getLocationName, defaultPropertyBindingsData, !!petsQuery.data]
  );

  const renderTemplate = ({ template, linkData, additionalBindings }: RenderTemplateArgs) => {
    const appointmentId = linkData?.find(
      (link) => link.relatedType === RelatedType.RELATED_TYPE_APPOINTMENT
    )?.relatedId;
    const latestAppointment = latestPastAppointment?.appointments?.length
      ? latestPastAppointment?.appointments?.[0]
      : undefined;
    const bindingsList = getBindingsList({
      [DynamicFieldProperty_Enum.PET_NAME]:
        getPetBindingFromAppointmentData(
          {
            allAppointments: latestAppointment ? [latestAppointment] : [],
            futureAppointmentCount: 0,
            latestAppointment,
          },
          petsQuery.data?.pets ?? [],
          appointmentId
        ) || '[Pet Name]',
      ...additionalBindings,
    });

    setRenderData(() => {
      const newPayload: Parameters<(typeof TemplatorV2Queries)['useRender']>[0] = {
        templateString: template.templateString,
        templateTypeSlug: template.templateTypeSlug,
        bindingsList,
        timezone,
        // TODO: get the locale dynamically once we have a way to do so
        locale: 'en_US',
        destinationType: DestinationType_Enum.SMS,
        keepEmptyDynamicFields: true,
      };

      if (renderedTemplateQuery.data && isEqual(newPayload, renderData?.payload)) {
        onRenderTemplate({
          bodyValue: renderedTemplateQuery.data.message.message,
          linkData: renderData?.linkData,
        });
      }

      return {
        payload: newPayload,
        linkData,
      };
    });
  };

  useEffect(() => {
    if (renderedTemplateQuery.data) {
      onRenderTemplate({
        bodyValue: renderedTemplateQuery.data.message.message,
        linkData: renderData?.linkData,
      });
    }
  }, [renderedTemplateQuery.data]);

  const onOpenSettings = () => {
    setHideModal(true);
  };

  useEffect(() => {
    if (!isOpen && hideModal) {
      setTimeout(() => {
        setHideModal(false);
      }, SETTINGS_MODAL_INTERACTION_DELAY);
    }
  }, [isOpen]);

  const manualTemplateProps = useManualTemplateFlow({
    groupId,
    renderTemplate,
    onOpenSettings,
  });
  const scheduleTemplateProps = useScheduleTemplateFlow({
    groupId,
    personId,
    renderTemplate,
    onOpenSettings,
  });
  const reviewInvitationProps = useReviewInvitationTemplateFlow({
    groupId,
    personId,
    renderTemplate,
    onOpenSettings,
  });
  const textToPayProps = useTextToPayTemplateFlow({
    groupId,
    personId,
    renderTemplate,
    onOpenSettings,
  });

  const {
    actionLabel: formsPromotionTooltipText,
    showFormsTeaser,
    isFormsActive,
    trackingId: formsPromotionTrackingId,
  } = useFormsTeaserConfig({
    locationIds: [groupId],
    source: 'inbox-thread-template',
  });

  const formsPromotionProps = useFormsPromotionFlow({
    tooltipText: formsPromotionTooltipText,
    trackingId: formsPromotionTrackingId,
  });

  const formsTemplateProps = useFormsTemplateFlow({
    disabled: !isFormsActive,
    groupId,
    personId,
    personPhone,
    renderTemplate,
    onOpenSettings,
  });

  const availableTemplateFlows = useMemo<TemplateFlowPopoverItem[]>(
    () =>
      [
        manualTemplateProps.popoverItem,
        scheduleTemplateProps.popoverItem,
        reviewInvitationProps.popoverItem,
        showFormsTeaser ? formsPromotionProps.popoverItem : formsTemplateProps.popoverItem,
        textToPayProps.popoverItem,
      ].filter<TemplateFlowPopoverItem>((item) => !!item),
    [
      manualTemplateProps.popoverItem,
      scheduleTemplateProps.popoverItem,
      reviewInvitationProps.popoverItem,
      showFormsTeaser,
      formsPromotionProps.popoverItem,
      formsTemplateProps.popoverItem,
      textToPayProps.popoverItem,
    ]
  );

  const templateFlowState = useMemo(
    () => ({
      availableTemplateFlows,
      person,
      isPrimaryContact: !!personId && personId === primaryContactId,
      canChangeContextContact,
      modalsProps: {
        manualTemplateModalProps: {
          ...manualTemplateProps.modalProps,
          show: hideModal ? 'hidden' : manualTemplateProps.modalProps.show,
          templateSelectorProps: {
            ...manualTemplateProps.modalProps.templateSelectorProps,
            settingsOpenDelay: SETTINGS_MODAL_INTERACTION_DELAY,
          },
        },
        scheduleTemplateModalProps: {
          ...scheduleTemplateProps.modalProps,
          show: hideModal ? 'hidden' : scheduleTemplateProps.modalProps.show,
          settingsOpenDelay: SETTINGS_MODAL_INTERACTION_DELAY,
        },
        reviewInvitationModalProps: {
          ...reviewInvitationProps.modalProps,
          show: hideModal ? 'hidden' : reviewInvitationProps.modalProps.show,
          templateSelectorProps: {
            settingsOpenDelay: SETTINGS_MODAL_INTERACTION_DELAY,
            ...reviewInvitationProps.modalProps.templateSelectorProps,
          },
        },
        selectInvoiceModalProps: {
          ...textToPayProps.modalProps,
          show: hideModal ? 'hidden' : textToPayProps.modalProps.show,
          settingsOpenDelay: SETTINGS_MODAL_INTERACTION_DELAY,
        },
        formsTemplateModalProps: {
          ...formsTemplateProps.modalProps,
          show: hideModal ? 'hidden' : formsTemplateProps.modalProps.show,
          settingsOpenDelay: SETTINGS_MODAL_INTERACTION_DELAY,
        },
        formsPromotionModalProps: formsPromotionProps.modalProps,
      },
    }),
    [
      availableTemplateFlows,
      person,
      personId,
      primaryContactId,
      canChangeContextContact,
      manualTemplateProps.modalProps,
      hideModal,
      scheduleTemplateProps.modalProps,
      reviewInvitationProps.modalProps,
      textToPayProps.modalProps,
      formsTemplateProps.modalProps,
      formsPromotionProps.modalProps,
    ]
  );

  return templateFlowState;
};
